import {
  UNIT_CUSTOM_FIELD_TYPES,
  type CreateUnitCustomFieldDefinitionInput,
  type UnitCustomFieldOption,
  type UnitCustomFieldType,
} from '@sitewise/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Input,
  Label,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import {
  archiveUnitFieldDefinition,
  createUnitFieldDefinition,
  listUnitFieldDefinitions,
  reorderUnitFieldDefinitions,
  restoreUnitFieldDefinition,
  type UnitFieldDefinition,
} from '../../../api/units.js';
import { ApiError } from '../../../lib/api-client.js';

const FIELD_TYPE_LABELS: Record<UnitCustomFieldType, string> = {
  text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  date: 'Date',
  yes_no: 'Yes / No',
  single_select: 'Single select',
  multi_select: 'Multi-select',
};

/**
 * Admin surface for the org's unit-custom-field catalog. Active fields
 * render at the top, archived fields collapse into a footer section.
 * Reorder happens via up/down arrows — no drag-and-drop dependency
 * since the existing codebase doesn't bundle one.
 */
export function UnitFieldsSettingsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const definitionsQuery = useQuery({
    queryKey: ['unit-fields', { includeArchived: true }],
    queryFn: () => listUnitFieldDefinitions({ includeArchived: true }),
  });

  const definitions = useMemo(
    () => definitionsQuery.data?.definitions ?? [],
    [definitionsQuery.data],
  );
  const active = useMemo(() => definitions.filter((d) => !d.isArchived), [definitions]);
  const archived = useMemo(() => definitions.filter((d) => d.isArchived), [definitions]);

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderUnitFieldDefinitions({ ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unit-fields'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not reorder');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveUnitFieldDefinition(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unit-fields'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreUnitFieldDefinition(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unit-fields'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not restore');
    },
  });

  function move(id: string, delta: 1 | -1) {
    const ids = active.map((d) => d.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Unit fields</h2>
          <p className="text-sm text-fg-secondary">
            Define custom fields that appear on every unit's Overview tab. Archive a field to
            hide it without losing previously entered values.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New field
        </Button>
      </div>

      {definitionsQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : definitionsQuery.isError ? (
        <ErrorState description="Could not load custom fields." />
      ) : active.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No custom fields yet. Add one to start collecting structured data on every unit.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {active.map((def, idx) => (
              <FieldRow
                key={def.id}
                def={def}
                isFirst={idx === 0}
                isLast={idx === active.length - 1}
                onMoveUp={() => move(def.id, -1)}
                onMoveDown={() => move(def.id, 1)}
                onArchive={() => {
                  if (confirm(`Archive "${def.label}"? Existing values are preserved.`)) {
                    archiveMutation.mutate(def.id);
                  }
                }}
                isBusy={reorderMutation.isPending}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {archived.length > 0 ? (
        <div className="space-y-2">
          <button
            type="button"
            className="text-sm text-fg-muted hover:text-fg"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide' : 'Show'} archived fields ({archived.length})
          </button>
          {showArchived ? (
            <Card>
              <CardContent className="divide-y divide-border p-0">
                {archived.map((def) => (
                  <div
                    key={def.id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-fg-secondary">{def.label}</div>
                      <div className="truncate text-xs text-fg-muted">
                        <code className="font-mono text-[11px]">{def.key}</code> ·{' '}
                        {FIELD_TYPE_LABELS[def.fieldType] ?? def.fieldType}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restoreMutation.mutate(def.id)}
                      disabled={restoreMutation.isPending}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      <CreateFieldDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        existingKeys={active.map((d) => d.key)}
      />
    </div>
  );
}

interface FieldRowProps {
  def: UnitFieldDefinition;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onArchive: () => void;
  isBusy: boolean;
}

function FieldRow({
  def,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onArchive,
  isBusy,
}: FieldRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
      <div className="min-w-0">
        <div className="truncate font-medium">{def.label}</div>
        <div className="truncate text-xs text-fg-muted">
          <code className="font-mono text-[11px]">{def.key}</code>
          {def.helpText ? <span className="ml-2">{def.helpText}</span> : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="outline" className="capitalize">
          {FIELD_TYPE_LABELS[def.fieldType] ?? def.fieldType}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Move up"
          disabled={isFirst || isBusy}
          onClick={onMoveUp}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Move down"
          disabled={isLast || isBusy}
          onClick={onMoveDown}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Archive field"
          className="text-danger hover:text-danger"
          onClick={onArchive}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface CreateFieldDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existingKeys: string[];
}

function CreateFieldDialog({ open, onOpenChange, existingKeys }: CreateFieldDialogProps) {
  const queryClient = useQueryClient();
  const labelId = useId();
  const keyId = useId();
  const typeId = useId();
  const helpId = useId();
  const optionsId = useId();
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [fieldType, setFieldType] = useState<UnitCustomFieldType>('text');
  const [helpText, setHelpText] = useState('');
  const [optionsText, setOptionsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Auto-derive a key from the label until the user edits the key
  // field directly. Reset when the dialog opens.
  const [keyTouched, setKeyTouched] = useState(false);
  useEffect(() => {
    if (!keyTouched) {
      const derived = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80);
      setKey(derived);
    }
  }, [label, keyTouched]);
  useEffect(() => {
    if (open) {
      setLabel('');
      setKey('');
      setFieldType('text');
      setHelpText('');
      setOptionsText('');
      setKeyTouched(false);
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (input: CreateUnitCustomFieldDefinitionInput) =>
      createUnitFieldDefinition(input),
    onSuccess: () => {
      toast.success('Custom field added.');
      void queryClient.invalidateQueries({ queryKey: ['unit-fields'] });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create field');
    },
  });

  function parseOptions(): UnitCustomFieldOption[] | null {
    const lines = optionsText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) return null;
    return lines.map((line) => {
      const [valuePart, ...rest] = line.split('|');
      const labelPart = rest.join('|').trim();
      const value = (valuePart ?? '').trim();
      return {
        value: value || labelPart,
        label: labelPart || value,
      };
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError('Label is required');
      return;
    }
    if (!key.trim()) {
      setError('Key is required');
      return;
    }
    if (existingKeys.includes(key)) {
      setError('Another active field already uses this key');
      return;
    }
    const isSelect = fieldType === 'single_select' || fieldType === 'multi_select';
    const options = isSelect ? parseOptions() : null;
    if (isSelect && (!options || options.length < 2)) {
      setError('Select fields need at least two options (one per line)');
      return;
    }
    create.mutate({
      key,
      label: label.trim(),
      fieldType,
      options,
      helpText: helpText.trim() || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New custom field</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={labelId}>Label</Label>
            <Input
              id={labelId}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Last carpet replaced"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={keyId}>Key</Label>
            <Input
              id={keyId}
              value={key}
              onChange={(e) => {
                setKeyTouched(true);
                setKey(e.target.value);
              }}
              placeholder="last_carpet_replaced"
            />
            <p className="text-xs text-fg-muted">
              Stable identifier used in audit logs and the API. Lowercase letters, digits, and
              underscores only.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={typeId}>Field type</Label>
            <select
              id={typeId}
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as UnitCustomFieldType)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            >
              {UNIT_CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <p className="text-xs text-fg-muted">
              The type cannot change later. Archive and recreate if you need a different shape.
            </p>
          </div>
          {(fieldType === 'single_select' || fieldType === 'multi_select') && (
            <div className="space-y-1">
              <Label htmlFor={optionsId}>Options</Label>
              <textarea
                id={optionsId}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                rows={4}
                placeholder={`one_per_line | One per line\nalt_value | Alt value`}
                className="w-full rounded border border-border bg-surface px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-fg-muted">
                One option per line. Use <code>value | Label</code> when they differ.
              </p>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor={helpId}>Help text (optional)</Label>
            <Input
              id={helpId}
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              placeholder="What should this field capture?"
            />
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create field'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
